import { computed } from 'vue'
import { useFetchBetslip } from '#src/composables/useFetchBetslip'
import type { Bet } from '@nsftx/seven-client-sdk-integrator'
import { BetType } from '#src/enums/BetType'

export const useBetslip = () => {
  const { betslip } = useFetchBetslip()

  const betslipId = computed(() => betslip.value?.id)
  const betslipCurrency = computed(() => betslip.value?.context.currency)
  const betslipTimestamp = computed(
    () => betslip.value?.activities.find((e) => e.type === 'CONFIRM_PLACE_BETSLIP')?.finishedAt
  )
  const betslipProductId = computed(() => betslip.value?.context.gameId)
  const betslipLinkCode = computed(() => betslip.value?.context.linkCode)
  const bets = computed(() => betslip.value?.bets)
  const betIds = computed(() => bets.value?.map(({ id }) => id))
  const feBetslipId = computed(() => betslip.value?.context.feBetslipId)
  const isMultibet = computed(() => bets.value?.length && bets.value.length > 1)

  const betslipPlacedAt = computed(() => {
    return (
      betslip.value?.activities.find((activity) => activity.type === 'PREPARE_PLACE_BETSLIP')
        ?.initiatorOccurredAt ?? ''
    )
  })

  const shouldShowCancelBetslip = computed(
    () => bets.value?.length && !bets.value.some((bet) => bet.status.resolutionStatus !== 'OPEN')
  )
  const shouldShowRebet = computed(
    () => bets.value?.length && bets.value.some((bet) => bet.status.resolutionStatus === 'OPEN')
  )
  const shouldShowPrintPayoutConfirmation = computed(
    () =>
      bets.value?.length &&
      betslip.value?.activities.some(
        (activity) =>
          activity.type === 'PAYOUT_BETS' && activity.currentState === 'PAYOUT_BETS_FINISHED'
      )
  )
  const shouldShowPayout = computed(() => {
    const wonBets = bets.value?.filter((bet) => bet.status.resolutionStatus === 'WON')

    if (!wonBets?.length) {
      return false
    }

    const isPayoutBetsFinished = betslip.value?.activities.some(
      (activity) =>
        activity.type === 'PAYOUT_BETS' &&
        activity.currentState === 'PAYOUT_BETS_FINISHED' &&
        wonBets.some((bet) => activity.payoutBetIds?.some((payoutBet) => payoutBet === bet.id))
    )

    if (isPayoutBetsFinished) {
      return false
    }

    return true
  })

  const betslipTotalPayout = computed<number>(
    () => bets.value?.reduce((acc, bet) => acc + Number(bet.pWinnings.max.total), 0) ?? 0
  )

  const getBetType = (bet: Bet | undefined) => {
    if (!bet) {
      return ''
    }

    const selectionCount = bet.selections.length
    const systemCount = bet.selectedSystems.length
    const firstSystem = bet.selectedSystems[0]

    if (selectionCount === 1 && systemCount === 1 && firstSystem === selectionCount) {
      return BetType.Single
    }

    if (
      selectionCount > 1 &&
      selectionCount < 64 &&
      systemCount === 1 &&
      firstSystem === selectionCount
    ) {
      return BetType.Combo
    }

    if (
      selectionCount > 1 &&
      (systemCount > 1 ||
        (systemCount === 1 && firstSystem !== undefined && firstSystem < selectionCount))
    ) {
      return BetType.System
    }

    return ''
  }

  return {
    getBetType,
    betslipTotalPayout,
    betslip,
    betslipId,
    betslipCurrency,
    betslipTimestamp,
    betslipProductId,
    betslipLinkCode,
    bets,
    betIds,
    feBetslipId,
    isMultibet,
    betslipPlacedAt,
    shouldShowCancelBetslip,
    shouldShowRebet,
    shouldShowPrintPayoutConfirmation,
    shouldShowPayout
  }
}
